import { MAP_NAMES, type Picture, type PictureId, PICTURES, type Sym, mapPicture } from "./pictures";
import type { Spec } from "./shared";
import { ANIMALS } from "./animals";

/**
 * مرحله‌های آفلاینِ فلش‌ها — تک‌نفره، تک‌رنگ و بی‌پایان (کاربر، ۷ آبان: «آفلاین مرحله‌ای و تک‌رنگ؛ آنلاین انتخاب
 * کند، افراد اضافه شوند و مرحله را شروع کنند»). مسابقه با دیگران فقط در اتاقِ دوستان است.
 *
 * ۲۴ مرحله‌ی اول نقش‌های دست‌سازند (دل، پروانه، لاله، ایوان، برج آزادی…). از ۲۵ به بعد هر مرحله نقشه‌ای است که از
 * شماره‌ی خودش ساخته می‌شود (mapPicture)، و هر چهار مرحله یک نقشِ دست‌ساز بزرگ‌تر برمی‌گردد. سختی با شماره بالا
 * می‌رود و سقف دارد: فلش‌های بیشتر، درازتر و پرپیچ‌تر. تخته‌ی هر مرحله هر بار از نو چیده می‌شود.
 *
 * سه قلب: لمسِ فلشِ بسته یک قلب می‌برد و بی قلب مرحله از نو. ستاره از قلب‌های مانده. مرحله‌ی بعد با تمام‌کردن باز
 * می‌شود. عددها حدس‌اند و باید با بازیِ آدم‌های واقعی تنظیم شوند.
 */
export interface Stage extends Spec {
  /** شماره‌ی مرحله از ۰. */
  index: number;
  /** شناسه‌ی نقش: نامِ نقشِ دست‌ساز یا map<شماره>. */
  key: string;
  name: string;
}

/** ۲۴ نقشِ دست‌ساز، به ترتیب؛ همین ترتیب PictureOrder ِ سرور و ArrowsStages ِ اپ است (شماره‌ی نقش در اتاق). */
export const HANDMADE: [PictureId, string][] = [
  ["khesht", "خشت"], ["plus", "چهارسو"], ["del", "دل"], ["golbarg", "گلبرگ"],
  ["pik", "پیک"], ["setareh", "ستاره"], ["mahi2", "دو ماهی"], ["halgheh", "حلقه"],
  ["gashniz", "گشنیز"], ["badbezan", "بادبزن"], ["anar", "انار"], ["shabdar", "شبدر"],
  ["parvaneh", "پروانه"], ["gorbeh", "گربه‌ها"], ["taj", "تاج"], ["mahiha", "ماهی‌ها"],
  ["goldan", "گلدان"], ["laleh", "لاله‌ها"], ["ivan", "ایوان"], ["mahha", "ماه‌ها"],
  ["boteh", "بته‌جقه"], ["lachak", "لچک ترنج"], ["hashtpar", "هشت‌پر"], ["azadi", "برج آزادی"],
];

/** نامِ فارسیِ یک نقشِ دست‌ساز (تخته‌ی آنلاین فقط شماره‌اش را دارد). */
export const pictureName = (id: string): string => HANDMADE.find((r) => r[0] === id)?.[1] ?? "";

const ANY: Sym[] = ["rot4", "mir4", "mir2", "rot2"];
const cache = new Map<number, Stage>();

/** مرحله‌ی i (از ۰)؛ هر عددی، بی سقف. */
export function stageAt(i: number): Stage {
  let st = cache.get(i);
  if (st) return st;
  let pic: Picture, key: string, name: string;
  if (i < HANDMADE.length) {
    const [id, fa] = HANDMADE[i]!;
    pic = PICTURES[id]; key = id; name = fa;
  } else if ((i - HANDMADE.length) % 4 === 3) {
    // نقشِ دست‌ساز برمی‌گردد، بزرگ‌تر و پرپیچ‌تر
    const [id, fa] = HANDMADE[Math.floor((i - HANDMADE.length) / 4) % HANDMADE.length]!;
    pic = PICTURES[id]; key = id; name = fa;
  } else {
    pic = mapPicture(i, ANY); key = pic.id; name = MAP_NAMES[i % MAP_NAMES.length]!;
  }
  st = {
    index: i, key, name, pic,
    arrows: Math.min(170, 24 + Math.round(2.6 * i)),
    len: Math.min(9, 4 + Math.floor(i / 5)),
    turn: Math.min(0.62, 0.36 + 0.006 * i),
  };
  cache.set(i, st);
  return st;
}

// ---- پیشرفت، در همین مرورگر ----

const KEY = "bazivan.arrows.stages";

export interface Progress {
  /** چند مرحله‌ی پشتِ هم را تمام کرده‌ای؛ مرحله‌ی بازِ بعدی همین شماره است. */
  level: number;
  /** بهترین زمانِ تمام‌کردنِ هر مرحله (میلی‌ثانیه)، به شماره‌ی مرحله. */
  best: Record<string, number>;
  /** بیشترین ستاره‌ی هر مرحله (۱ تا ۳). */
  stars: Record<string, number>;
}

export function loadProgress(): Progress {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? "null") as (Partial<Progress> & { won?: unknown }) | null;
    // نسخه‌ی پیشین فهرستِ شناسه‌های برده را نگه می‌داشت
    const level = typeof v?.level === "number" ? v.level : Array.isArray(v?.won) ? v.won.length : 0;
    const obj = (x: unknown) => (x && typeof x === "object" ? (x as Record<string, number>) : {});
    return { level: Math.max(0, Math.floor(level)), best: obj(v?.best), stars: obj(v?.stars) };
  } catch {
    return { level: 0, best: {}, stars: {} };
  }
}

/** پایانِ یک مرحله: تمام شد (با زمان و ستاره) یا قلب‌ها تمام شد (stars ۰). */
export function saveStage(i: number, stars: number, finishedAt: number | null): Progress {
  const p = loadProgress();
  const k = String(i);
  if (stars > 0) {
    p.level = Math.max(p.level, i + 1);
    p.stars[k] = Math.max(p.stars[k] ?? 0, stars);
    if (finishedAt !== null && !(p.best[k]! <= finishedAt)) p.best[k] = Math.round(finishedAt);
  }
  try { localStorage.setItem(KEY, JSON.stringify(p)); } catch { /* حالتِ ناشناس: بی ذخیره هم بازی می‌شود */ }
  return p;
}

// ---- نقشه‌های بزرگِ جانوران (animals.ts): همیشه باز، هر کدام ستاره و بهترین زمانِ خودش ----

const BIG_KEY = "bazivan.arrows.big";
export type BigProgress = Record<string, { stars: number; best?: number }>;

export function loadBig(): BigProgress {
  try {
    const v = JSON.parse(localStorage.getItem(BIG_KEY) ?? "null") as BigProgress | null;
    return v && typeof v === "object" ? v : {};
  } catch {
    return {};
  }
}

/** پایانِ یک نقشه‌ی بزرگ: ستاره (بیشترین می‌ماند) و بهترین زمان؛ باخت (stars ۰) چیزی را عوض نمی‌کند. */
export function saveBig(id: string, stars: number, finishedAt: number | null): BigProgress {
  const p = loadBig();
  if (stars > 0) {
    const was = p[id];
    p[id] = {
      stars: Math.max(was?.stars ?? 0, stars),
      best: finishedAt !== null && !(was?.best !== undefined && was.best <= finishedAt) ? Math.round(finishedAt) : was?.best,
    };
  }
  try { localStorage.setItem(BIG_KEY, JSON.stringify(p)); } catch { /* حالتِ ناشناس */ }
  return p;
}

/** مرحله‌ی i باز است اگر تا پیش از آن را تمام کرده باشی. */
export const isOpen = (p: Progress, i: number): boolean => i <= p.level;

/** همان‌جا که بازیکن رسیده: اولین مرحله‌ی تمام‌نشده. */
export const frontier = (p: Progress): number => p.level;

// ---- آلبومِ نقش‌ها: هر نقشِ دست‌ساز و هر جانوری که دست‌کم یک بار تمام شده، کاشی‌کاری‌شده ----

/** یک خانه‌ی آلبوم: نقشِ دست‌ساز (stage = مرحله‌ی اولی که این نقش را دارد) یا جانور. */
export interface AlbumSlot { key: string; name: string; stage?: number; animal?: string }

export const ALBUM: readonly AlbumSlot[] = [
  ...HANDMADE.map(([id, name], i) => ({ key: id, name, stage: i })),
  ...ANIMALS.map((a) => ({ key: `a:${a.id}`, name: a.name, animal: a.id })),
];

/** کلیدِ آلبومِ مرحله‌ی i؛ نقشه‌ی ساخته‌شده (map…) در آلبوم نیست. */
export function albumKeyOf(i: number): string | undefined {
  const k = stageAt(i).key;
  return HANDMADE.some((r) => r[0] === k) ? k : undefined;
}

/**
 * نقش‌های آلبوم که تمام شده‌اند: هر مرحله‌ی بُرده (زیرِ level؛ پیشرفتِ کهنه ستاره ندارد) که نقشِ دست‌ساز دارد — برگشتِ
 * بزرگ‌ترش هم — و جانورِ با ستاره. همان ArrowsProgress.AlbumDone ِ اپ.
 */
export function albumDone(p: Progress, big: BigProgress): Set<string> {
  const done = new Set<string>();
  for (let i = 0; i < p.level; i++) {
    const key = albumKeyOf(i);
    if (key) done.add(key);
  }
  for (const [id, v] of Object.entries(big)) if (v.stars > 0) done.add(`a:${id}`);
  return done;
}
